import { Box, List, Skeleton } from '@mui/material';

import { Dimens } from '~themes';

/**
 * Placeholder shown while the user's roles are still loading, so the menu does
 * not first render a partial (unauthorized) list and then jump once roles
 * arrive. Mirrors the real layout: a scrollable top group and a bottom-pinned
 * group, so the logo and pinned area stay put through the swap.
 */

type Line = { id: string; kind: 'row'; width: string | number } | { id: string; kind: 'header' };

// A representative shape: a few rows broken up by a couple of section headers.
const topLines: Line[] = [
	{ id: 't1', kind: 'row', width: Dimens.fixed(96) },
	{ id: 't2', kind: 'header' },
	{ id: 't3', kind: 'row', width: Dimens.fixed(120) },
	{ id: 't4', kind: 'row', width: Dimens.fixed(80) },
	{ id: 't5', kind: 'row', width: Dimens.fixed(64) },
	{ id: 't6', kind: 'row', width: Dimens.fixed(104) },
	{ id: 't7', kind: 'header' },
	{ id: 't8', kind: 'row', width: Dimens.fixed(112) },
	{ id: 't9', kind: 'row', width: Dimens.fixed(72) },
];
const bottomLines = [
	{ id: 'b1', width: Dimens.fixed(72) },
	{ id: 'b2', width: Dimens.fixed(110) },
	{ id: 'b3', width: Dimens.fixed(84) },
];

const SkeletonRow = ({ showLabel, width }: { showLabel: boolean; width: string | number }) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: Dimens.spacingMd,
			px: Dimens.spacingMd,
			height: showLabel ? '36px' : '58px',
			justifyContent: showLabel ? 'flex-start' : 'center',
		}}
	>
		<Skeleton
			variant='circular'
			width={showLabel ? Dimens.spacing2xl : Dimens.spacing3xl}
			height={showLabel ? Dimens.spacing2xl : Dimens.spacing3xl}
		/>
		{showLabel && <Skeleton variant='rounded' width={width} height={Dimens.spacingLg} />}
	</Box>
);

const SkeletonHeader = () => (
	<Box sx={{ px: Dimens.spacingMd, pt: Dimens.spacingLg, pb: Dimens.spacingXs }}>
		<Skeleton variant='rounded' width={Dimens.fixed(56)} height={Dimens.fixed(10)} />
	</Box>
);

const SideMenuSkeleton = ({ showLabel }: { showLabel: boolean }) => (
	<>
		<List
			aria-hidden
			disablePadding
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: Dimens.spacingXs,
				paddingTop: Dimens.spacingLg,
			}}
		>
			{topLines.map((line) =>
				line.kind === 'header' ?
					showLabel ? <SkeletonHeader key={line.id} />
					:	null
				:	<SkeletonRow key={line.id} showLabel={showLabel} width={line.width} />
			)}
		</List>
		<List
			aria-hidden
			disablePadding
			sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: Dimens.spacingXs }}
		>
			{bottomLines.map((line) => (
				<SkeletonRow key={line.id} showLabel={showLabel} width={line.width} />
			))}
		</List>
	</>
);

export default SideMenuSkeleton;
